import classNames from 'classnames';
import type { TCompensationVisibilityModalProps } from '@components/_modals/CompensationVisibilityModal/CompensationVisibilityModal.types';
import { MODAL_IDS_MAP } from '@app/constants/modals';
import {
  COMPENSATION_VISIBILITY_KEY,
  COMPENSATION_VISIBILITY_VALUES,
} from '@app/constants/common';
import { useStoreActions, useStoreState } from 'easy-peasy';
import type { TShowcaseModel } from '@app/models/Showcase';
import { ACTION_VARIANTS, Button, ICON_NAMES } from '@reverse-hr/design-system';
import { Trans, useTranslation } from 'react-i18next';
import type { FC } from 'react';

export const CompensationVisibilityModal: FC<
  TCompensationVisibilityModalProps
> = props => {
  const { isOpen, modalId, onClose } = props;
  const isCompensationVisible = useStoreState(
    (state: TShowcaseModel) => state.showcaseTable.isCompensationVisible,
  );
  const toggleCompensationVisibilityStatus = useStoreActions(
    (state: TShowcaseModel) =>
      state.showcaseTable.toggleCompensationVisibilityStatus,
  );

  const { t } = useTranslation('', {
    keyPrefix: 'globalCompensation.compensationVisibilityModal',
  });

  const open = isOpen && modalId === MODAL_IDS_MAP.COMPENSATION_VISIBILITY;

  const containerClassName = classNames(
    'c-app-modal c-app-modal--template c-app-modal--slide c-compensation-visibility-modal',
    {
      'is-open': open,
    },
  );

  const handleCompensationVisibility = (): void => {
    const visibilityValue = localStorage.getItem(COMPENSATION_VISIBILITY_KEY);
    const compensationVisibility =
      visibilityValue === COMPENSATION_VISIBILITY_VALUES.HIDDEN
        ? COMPENSATION_VISIBILITY_VALUES.VISIBLE
        : COMPENSATION_VISIBILITY_VALUES.HIDDEN;

    localStorage.setItem(COMPENSATION_VISIBILITY_KEY, compensationVisibility);

    toggleCompensationVisibilityStatus({
      compensationVisibility,
    });

    onClose();
  };

  return (
    <div
      className={containerClassName}
      id="modal-compensation-visibility"
      aria-hidden={!open}
    >
      <div
        className="c-app-modal__overlay"
        tabIndex={-1}
        data-micromodal-close=""
      >
        <div
          className="c-app-modal__container"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-delete-template-title"
        >
          <div className="c-app-modal__inner">
            <div className="c-compensation-visibility-modal__header">
              <Button
                onClick={onClose}
                variant={ACTION_VARIANTS.GHOST}
                leftIcon={ICON_NAMES.CLOSE_BIG}
              ></Button>
            </div>
            <div className="c-compensation-visibility-modal__content">
              <h2 className="c-compensation-visibility-modal__title">
                {t('title.value', {
                  context: isCompensationVisible ? 'hide' : 'show',
                })}
              </h2>
              <p>
                <Trans
                  i18nKey="globalCompensation.compensationVisibilityModal.text.value"
                  components={{
                    strong: <strong />,
                    br: <br />,
                  }}
                  context={isCompensationVisible ? 'hide' : 'show'}
                />
              </p>
            </div>
            <div className="c-compensation-visibility-modal__footer">
              <Button variant={ACTION_VARIANTS.OUTLINED} onClick={onClose}>
                {t('buttons.cancel')}
              </Button>
              <Button onClick={handleCompensationVisibility}>
                {t('buttons.confirm.value', {
                  context: isCompensationVisible ? 'hide' : 'show',
                })}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
